/**
 * VISTOERA_AI_CHAT_CONTROL_PLANE_P0 — Platform Admin
 * "AI 管理 → AI 会话" page.
 *
 * A Message-centric view of the same underlying data the Task List
 * shows (spec §3: aggregation, not a new table) — one row per Message,
 * each expandable into its up-to-3 sibling provider Executions. This is
 * the "Conversation Aggregate View" surfaced as its own dedicated nav
 * entry so an Admin thinking in terms of "which conversations/messages
 * had trouble" doesn't have to first switch the Task List's view
 * toggle.
 */
import type { TableColumnsType } from 'antd'
import { Empty, Input, Table, Tag } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { platformGetAIChatConversationDetail, platformListAIChatConversationAggregate } from '../api'
import type { AIChatAdminConversationAggregateRow, AIChatAdminConversationDetail } from '../types'
import { ProviderBadge, StatusBadge, formatDateTime } from './adminShared'
import { platformColors } from './theme'

export default function AIChatConversationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Number(searchParams.get('page') || '1') || 1
  const pageSize = Number(searchParams.get('page_size') || '20') || 20
  const keyword = searchParams.get('keyword') || ''
  const selectedConversationId = searchParams.get('conversation_id') || null

  const [draftKeyword, setDraftKeyword] = useState(keyword)
  useEffect(() => setDraftKeyword(keyword), [keyword])

  const [data, setData] = useState<{ items: AIChatAdminConversationAggregateRow[]; total: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    platformListAIChatConversationAggregate({ page, page_size: pageSize, keyword: keyword || undefined })
      .then((r) => {
        setData({ items: r.data.items, total: r.data.total })
        setError(null)
      })
      .catch((e) => setError(e?.response?.data?.detail?.message ?? e?.message ?? '加载失败'))
      .finally(() => setLoading(false))
  }, [page, pageSize, keyword])

  useEffect(() => load(), [load])

  const [detail, setDetail] = useState<AIChatAdminConversationDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  useEffect(() => {
    if (!selectedConversationId) {
      setDetail(null)
      return
    }
    setDetailLoading(true)
    platformGetAIChatConversationDetail(selectedConversationId)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false))
  }, [selectedConversationId])

  const patch = (p: Record<string, string | null>, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      for (const [k, v] of Object.entries(p)) {
        if (v === null || v === '') next.delete(k)
        else next.set(k, v)
      }
      if (resetPage) next.delete('page')
      return next
    })
  }

  const columns: TableColumnsType<AIChatAdminConversationAggregateRow> = [
    { title: 'Conversation', dataIndex: 'conversation_title', width: 180 },
    { title: 'Tenant', dataIndex: 'tenant_name', width: 140, render: (v: string | null) => v ?? '—' },
    { title: 'Message Summary', dataIndex: 'message_summary', ellipsis: true },
    { title: '聚合状态', dataIndex: 'aggregate_label', width: 160, render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Created At', dataIndex: 'created_at', width: 150, render: (v: string | null) => formatDateTime(v) },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0, fontSize: 18, color: platformColors.primaryText }}>AI 会话</h2>
        <Input.Search
          style={{ width: 260 }}
          placeholder="Conversation/Message ID 或关键字"
          value={draftKeyword}
          onChange={(e) => setDraftKeyword(e.target.value)}
          onSearch={(v) => patch({ keyword: v || null })}
          allowClear
        />
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '2 1 480px', minWidth: 360, background: platformColors.surface, border: `1px solid ${platformColors.border}`, borderRadius: 8, overflow: 'hidden' }}>
          {error ? (
            <div style={{ padding: 24 }}>
              <Empty description={error} />
            </div>
          ) : (
            <Table
              className="pf-data-table"
              rowKey="message_id"
              size="small"
              loading={loading}
              dataSource={data?.items ?? []}
              columns={columns}
              onRow={(r) => ({
                onClick: () => patch({ conversation_id: r.conversation_id }, false),
                style: { cursor: 'pointer', background: r.conversation_id === selectedConversationId ? 'var(--pf-primary-soft)' : undefined },
              })}
              pagination={{
                current: page,
                pageSize,
                total: data?.total ?? 0,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
                onChange: (p, ps) => patch({ page: String(p), page_size: String(ps) }, false),
              }}
            />
          )}
        </div>

        <div style={{ flex: '1 1 380px', minWidth: 320, background: platformColors.surface, border: `1px solid ${platformColors.border}`, borderRadius: 8, padding: 12 }}>
          {!selectedConversationId ? (
            <Empty description="选择左侧一条会话以查看完整对话" />
          ) : detailLoading || !detail ? (
            <span style={{ color: platformColors.mutedText, fontSize: 12 }}>加载中…</span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 560, overflow: 'auto' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{detail.title}</div>
              <div style={{ fontSize: 12, color: platformColors.mutedText }}>{detail.tenant_name ?? '—'}</div>
              {detail.messages.map((m) => (
                <div key={m.id} style={{ border: `1px solid ${platformColors.border}`, borderRadius: 6, padding: 8 }}>
                  <div style={{ fontSize: 11, color: platformColors.mutedText }}>
                    {m.role} · {formatDateTime(m.created_at)}
                  </div>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', marginTop: 4 }}>{m.text}</div>
                  {m.executions.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                      {m.executions.map((ex) => (
                        <span key={ex.id} style={{ display: 'inline-flex', gap: 4 }}>
                          <ProviderBadge provider={ex.provider} label={ex.provider_label} />
                          <StatusBadge status={ex.status} label={ex.status} />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
